import debounce from 'lodash/debounce';
import get from 'lodash/get';
import throttle from 'lodash/throttle';
import React, { Component } from 'react';
import IDS from '../../../constants/IDS';
import { getBoundingClientRect, getDistance, getMiddlePoint } from '../../../helpers/coordinates';
import { findMinMaxCoordinates, getEventCoordinates } from '../../../helpers/touchEvents';
import { isCategory, shouldDraggingStart, isStandingSector } from '../../../helpers/mouseEvents';
import transliterate from '../../../helpers/transliterate';
import { ITicketsControllerTicket } from '../../../types/ticket';
import EVENT_TYPES from '../../../constants/EVENT_TYPES';
import { META_EVENTS } from '../../../constants/CONTROLLERS_EVENT_TYPES';
import { STATUS_ICONS_NAMES } from '../../../constants/STATUS_ICONS';
import { resolve } from 'inversify-react';
import { StateController } from '../../../lib/widget/state/state.controller';
import type { IStateController } from '../../../lib/widget/state/state.interface';
import type { IZoomController } from '../../../lib/widget/zoom/zoom.interface';
import { STATE_MODULE_IDS } from '../../../lib/widget/state/state.types';
import { ZOOM_MODULE_IDS } from '../../../lib/widget/zoom/zoom.types';
import { TICKETS_MODULE_IDS } from '../../../lib/widget/tickets/tickets.types';
import type { ITicketsController } from '../../../lib/widget/tickets/tickets.interface';
import type { IPopoverController } from '../../../lib/widget/popover/popover.interface';
import { POPOVER_MODULE_IDS } from '../../../lib/widget/popover/popover.types';

class SchemeComponent extends Component<any, any> {
  widget;
  seatStartX: number;
  seatStartY: number;
  availableDiffX: number = 0;
  availableDiffY: number = 0;
  startDistance: number = null;
  layout: HTMLElement;
  middlePoint: {
    x: number;
    y: number;
  };
  touchDistance: number;
  diffX: number;
  diffY: number;
  minimapOnDragCallback: (diffX: number, diffY: number) => void;
  newZoom: number;
  focusedSeat: any;
  touches: Array<number> = [];
  startTouch: boolean = false;
  withEventListeners = false;
  dragXoffset = 0;
  dragYoffset = 0;

  stateController: IStateController;
  zoomController: IZoomController;
  ticketsController: ITicketsController;
  popoverController: IPopoverController;

  @resolve(StateController) StateController;

  constructor(props, container) {
    super(props);

    this.stateController = container.get(STATE_MODULE_IDS.STATE_CONTROLLER);
    this.zoomController = container.get(ZOOM_MODULE_IDS.ZOOM_CONTROLLER);
    this.ticketsController = container.get(TICKETS_MODULE_IDS.TICKETS_CONTROLLER);
    this.popoverController = container.get(POPOVER_MODULE_IDS.POPOVER_CONTROLLER);

    this.stateController.addListener(
      this.stateController.events.UPDATE,
      this.onUpdatestateController,
    );
    this.zoomController.addListener(this.zoomController.events.UPDATE, this.onUpdateZoom);
    window.Widget.metaController.addListener(META_EVENTS.INIT, this.updateLayout);
  }

  componentDidUpdate() {
    if (this.props.isFailure) {
      this.layout.removeEventListener('touchstart', this.handleTouchStart);
      this.layout.removeEventListener('touchmove', this.handleTouchMove);
      this.layout.removeEventListener('touchend', this.handleTouchEnd);
      this.layout.removeEventListener('touchcancel', this.handleTouchCancel);

      this.layout.removeEventListener('mousedown', this.handleMouseDown);
      // this.layout.removeEventListener('mouseleave', this.popoverController.hide);
      window.removeEventListener('mousemove', this.handleMouseMove);
      window.removeEventListener('mouseup', this.handleMouseUp);
      document.removeEventListener('mouseleave', this.handleMouseUp);
      this.withEventListeners = false;
    }

    if (!this.props.isFailure && !this.withEventListeners) {
      this.bindEventListeners();
    }
  }

  getOverlay = (node: HTMLElement) => {
    if (!node) return;
    this.layout = node;
    window.Widget.layout = node;

    window.Widget.createSchemeCanvas(this.layout);

    this.bindEventListeners();

    // track layout resizes
    const resizeObserver = new ResizeObserver(debounce(this.resizeScheme, 300));
    resizeObserver.observe(node);

    this.stateController.addListener(
      this.stateController.events.WIDGET_RERENDER,
      debounce(this.resizeScheme, 300),
    );
  };

  bindEventListeners = () => {
    this.layout.addEventListener('touchstart', this.handleTouchStart);
    this.layout.addEventListener('touchmove', this.handleTouchMove);
    this.layout.addEventListener('touchend', this.handleTouchEnd);
    this.layout.addEventListener('touchcancel', this.handleTouchCancel);

    this.layout.addEventListener('mousedown', this.handleMouseDown);
    // this.layout.addEventListener('mouseleave', this.popoverController.hide);
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
    document.addEventListener('mouseleave', this.handleMouseUp);
    this.withEventListeners = true;
  };

  onUpdatestateController = () => {
    if (
      this.stateController.isDragging &&
      this.focusedSeat &&
      !this.popoverController.state.isHidden
    ) {
      this.popoverController.hide();
    }
  };

  onUpdateZoom = () => {
    if (!this.popoverController.state.isHidden) {
      this.popoverController.hide();
    }
  };

  updateLayout = () => {
    if (window.Widget.metaController.meta.isPeriodical) {
      this.forceUpdate();
      this.setState(() => ({
        isPeriodical: true,
      }));

      setTimeout(() => {
        const canvas = document.getElementById(IDS.staticCanvas);

        if (!canvas) return;

        const { top: offsetTop } = getBoundingClientRect(canvas);

        window.Widget.offsetTop = offsetTop;
      }, 0);
    }
  };

  resizeScheme = () => {
    const { Widget } = window;
    if (!Widget.schemeMode || !this.stateController.schemeIsLoaded) return;

    const nodeBbox = getBoundingClientRect(this.layout);
    const { isFullscreen } = this.props;
    const canvas = document.getElementById(IDS.staticCanvas);
    const seatsCanvas = document.getElementById(IDS.canvas);

    if (!canvas) return;

    const { top: offsetTop, left: offsetLeft } = getBoundingClientRect(canvas);
    const widgetWidth = nodeBbox.width;
    const widgetHeight = nodeBbox.height;

    canvas.style.width = `${widgetWidth}px`;
    canvas.style.height = `${widgetHeight}px`;
    seatsCanvas.style.width = `${widgetWidth}px`;
    seatsCanvas.style.height = `${widgetHeight}px`;

    Widget.resize({
      widgetWidth,
      widgetHeight,
      offsetTop,
      offsetLeft,
      isFullscreen,
    });

    setTimeout(() => {
      this.stateController.unlock();
    }, 300);
  };

  // / / / / / / / / / / / / / / /  Mouse  // / / / / / / / / / / / / / / /
  handleMouseDown = (event: MouseEvent) => {
    if (this.zoomController.isZooming || event.button !== 0 || this.stateController.isLocked) {
      return;
    }

    const { pageX, pageY } = getEventCoordinates(event);

    window.Widget.draggingController.startX = pageX;
    window.Widget.draggingController.startY = pageY;

    this.initMouseDragEvents();
  };

  handleMouseMove = (event: MouseEvent) => {
    if (!this.zoomController.zoomIsMinimum) {
      this.showAvailableSeats(event);
    }
  };

  handleMouseUp = throttle((event: MouseEvent) => {
    if (this.stateController.state.isMinimapMoving) return;
    const target = event.target as HTMLElement;
    if (event.button !== 0 || !this.layout.contains(target)) return;
    this.terminateMouseDragEvents();

    this.onPointerUp(event);
  }, 250);

  handleMouseDrag = (event: MouseEvent) => {
    this.initSchemeDraging(event, true);
  };

  handleMouseDragEnd = (event: MouseEvent) => {
    if (event.button !== 0) return;

    this.terminateMouseDragEndEvents();
    this.terminateMouseDragEvents();

    this.endMoveScheme();
  };

  onStandMouseUp(target: HTMLElement) {
    const id = target.getAttribute('data-id');
    const sector = this.ticketsController.sectors.find((s) => s.id === id);

    if (sector?.meta?.tickets?.length > 1) {
      window.Widget.ticketsPopoverController.showStandPopover(sector);
    } else {
      this.ticketsController.showTicketsCountModal(id);
    }
  }

  onCategoryMouseUp(target: HTMLElement) {
    this.onCategoryPointerUp(target);
  }

  // / / / / / / / / / / / / / / /  binding mouse events
  initMouseDragEvents() {
    window.addEventListener('selectstart', this.disableSelect);
    window.addEventListener('mousemove', this.handleMouseDrag);

    window.removeEventListener('mousemove', this.handleMouseMove);
  }

  initMouseDragEndEvents() {
    window.addEventListener('mouseup', this.handleMouseDragEnd);
    window.removeEventListener('mouseup', this.handleMouseUp);
  }

  // / / / / / / / / / / / / / / /  unbinding mouse events
  terminateMouseDragEvents() {
    window.removeEventListener('selectstart', this.disableSelect);
    window.removeEventListener('mousemove', this.handleMouseDrag);

    window.addEventListener('mousemove', this.handleMouseMove);
  }

  terminateMouseDragEndEvents() {
    window.addEventListener('mouseup', this.handleMouseUp);

    window.removeEventListener('mouseup', this.handleMouseDragEnd);
  }

  // / / / / / / / / / / / / / / /  Touch  // / / / / / / / / / / / / / / /
  handleTouchStart = (event: TouchEvent) => {
    event.preventDefault();

    if (event.touches.length > 2) return;
    if (this.stateController.isZooming || this.stateController.isLocked) {
      return;
    }

    const { pageX, pageY } = getEventCoordinates(event);
    this.startTouch = true;
    window.Widget.draggingController.startX = pageX;
    window.Widget.draggingController.startY = pageY;

    if (event.touches.length === 2) {
      this.twoFingersZoomInit(event);
    } else if (window.Widget.metaController.canShowMobilePopup) {
      this.showAvailableSeats(event, { isTouch: true });
    }
  };

  handleTouchMove = (event: TouchEvent) => {
    event.preventDefault();
    if (this.stateController.isZooming) return;

    this.initSchemeDraging(event);
  };

  handleTouchDragEnd = (event) => {
    if (event.touches.length) return;

    this.endMoveScheme();
    this.terminateTouchDragEndEvents();
  };

  handleTouchEnd = (event: TouchEvent) => {
    if (this.startTouch) {
      event.preventDefault();
      this.onPointerUp(event);
    }
  };

  onStandTouchEnd(target: HTMLElement) {
    const id = target.getAttribute('data-id');

    this.ticketsController.showTicketsCountModal(id);
  }

  onCategoryTouchEnd(target: HTMLElement) {
    this.onCategoryPointerUp(target);
  }

  handleTouchCancel = (event: TouchEvent) => {
    event.preventDefault();
    this.reset();
  };

  twoFingersZoomInit(event: TouchEvent) {
    this.stateController.setState({ isZooming: true }, true);

    const p1 = event.touches[0];
    const p2 = event.touches[1];

    [p1, p2].forEach((touch) => this.touches.push(touch.identifier));
    this.middlePoint = getMiddlePoint(p1.pageX, p1.pageY, p2.pageX, p2.pageY);

    if (this.middlePoint) {
      this.startDistance = Math.sqrt(
        Math.pow(p2.pageX - p1.pageX, 2) + Math.pow(p2.pageY - p1.pageY, 2),
      );
      this.touchDistance = 0;

      this.init2FingersZoomEvents();
    }
  }

  handle2FingersZoom = (event: TouchEvent) => {
    event.preventDefault();
    if (event.touches.length !== 2) return;

    const coordinates = findMinMaxCoordinates(event);
    const currentDistance = getDistance(
      coordinates.minX,
      coordinates.minY,
      coordinates.maxX,
      coordinates.maxY,
    );
    const { currentZoom } = this.zoomController;
    const { staticCanvas, seatsCanvas } = window.Widget;

    const p1 = event.touches[0];
    const p2 = event.touches[1];
    const distance = Math.sqrt(Math.pow(p2.pageX - p1.pageX, 2) + Math.pow(p2.pageY - p1.pageY, 2));

    const { x, y } = getMiddlePoint(p1.pageX, p1.pageY, p2.pageX, p2.pageY);
    const diffX = x - this.middlePoint.x;
    const diffY = y - this.middlePoint.y;
    this.dragXoffset = diffX;
    this.dragYoffset = diffY;

    this.touchDistance = distance;

    const scalePixelChange = currentDistance - this.startDistance;

    this.newZoom = currentZoom + scalePixelChange * 0.01 * currentZoom * 0.7;

    const scaleFactor = currentZoom / this.newZoom;
    const maxZoom = this.zoomController.getMaximumZoom();
    const minZoom = this.zoomController.getMinimumZoom();
    const minWidth = window.Widget.schemeParameters.schemeWidth * minZoom;
    const maxWidth = window.Widget.schemeParameters.schemeWidth * maxZoom;
    const newWidth = window.Widget.schemeParameters.schemeWidth * this.newZoom;

    if (this.newZoom < minZoom) {
      this.newZoom = minZoom;
    }

    if (this.newZoom > maxZoom) {
      this.newZoom = maxZoom;
    }

    if (newWidth < maxWidth && newWidth > minWidth) {
      this.zoomNode(staticCanvas, scaleFactor);
      this.zoomNode(seatsCanvas, scaleFactor);
    }
  };

  handle2FingersZoomEnd = (event: TouchEvent) => {
    event.stopPropagation();
    // no fingers on screen -> end 2fingers zoom
    if (event.touches.length <= 1) {
      this.terminate2FingersZoomEvents();

      window.Widget.dragXoffset = this.dragXoffset + (this.diffX || 0);
      window.Widget.dragYoffset = this.dragYoffset + (this.diffY || 0);

      this.zoomController.setZoom({
        x: this.middlePoint.x,
        y: this.middlePoint.y + window.Widget.offsetTop,
        zoom: this.newZoom,
      });
      this.zoomController.stopZooming();

      this.reset();
      this.stateController.setState({ isZooming: false }, true);
    }
  };

  // / / / / / / / / / / / / / / /  binding touch events
  init2FingersZoomEvents() {
    this.layout.removeEventListener('touchmove', this.handleTouchMove);
    this.layout.removeEventListener('touchend', this.handleTouchEnd);

    this.layout.addEventListener('touchmove', this.handle2FingersZoom);
    document.addEventListener('touchend', this.handle2FingersZoomEnd);
  }

  initTouchDragEndEvents() {
    this.layout.removeEventListener('touchend', this.handleTouchEnd);
    this.layout.addEventListener('touchend', this.handleTouchDragEnd);
  }

  // / / / / / / / / / / / / / / /  unbinding touch events
  terminate2FingersZoomEvents() {
    this.layout.addEventListener('touchmove', this.handleTouchMove);
    this.layout.addEventListener('touchend', this.handleTouchEnd);

    this.layout.removeEventListener('touchmove', this.handle2FingersZoom);
    document.removeEventListener('touchend', this.handle2FingersZoomEnd);
  }

  terminateTouchDragEndEvents() {
    this.layout.addEventListener('touchend', this.handleTouchEnd);
    this.layout.removeEventListener('touchend', this.handleTouchDragEnd);
  }

  // / / / / / / / / / / / / / / /  utils  // / / / / / / / / / / / / / / /
  showAvailableSeats(
    event,
    options: { isTouch?: boolean; isMarketplace?: boolean } = {
      isTouch: false,
      isMarketplace: false,
    },
  ) {
    const { isTouch } = options;

    const { pageX, pageY } = getEventCoordinates(event);
    const { state: popoverState } = this.popoverController;
    const seatsInfoId = popoverState?.data?.seat?.id;

    // TODO: replace with fired events. This don't need in WidgetPreview
    this.focusedSeat =
      window.Widget.findAndRenderSeat && window.Widget.findAndRenderSeat({ pageX, pageY });

    if (!this.focusedSeat && !this.popoverController.state.isHidden) {
      this.popoverController.hide();
      return;
    }

    if (this.focusedSeat && seatsInfoId !== this.focusedSeat.id) {
      const isfocusedSeatAreSelected = Boolean(
        this.ticketsController.findTicketInSelected(this.focusedSeat),
      );

      if ((!isTouch || (isTouch && !isfocusedSeatAreSelected)) && window.Widget.schemeMode) {
        const seatDataForPopover = this.getSeatDataForPopover(this.focusedSeat);
        this.popoverController.show(seatDataForPopover);
      }
    }
  }
  // TODO: relocate to PopoverController
  getSeatDataForPopover(focusedSeat: ITicketsControllerTicket) {
    const isLanding = window.Widget.settings.isLanding;

    const { x, y } = window.Widget.transformPoint({
      x: focusedSeat.x,
      y: focusedSeat.y,
      inverseMatrix: false,
    });

    const subtitle: Array<any> = [<span>{transliterate(focusedSeat.sector)}</span>];
    let seatLabel: string;
    let text: string;

    if (focusedSeat.row && focusedSeat.seat) {
      const rowLabel = this.props.t('rowNumber', {
        number: focusedSeat.row,
      });
      seatLabel = this.props.t('seatNumber', {
        number: focusedSeat.seat,
      });

      text = isLanding ? `${rowLabel} · ${seatLabel}` : `${rowLabel}, ${seatLabel}`;
    }

    const footer = [];
    if (focusedSeat.tt) {
      let typeLabel;
      switch (focusedSeat.tt) {
        case 1:
          typeLabel = this.props.t('digitalTicket');
          break;
        case 2:
          typeLabel = this.props.t('paperTicket');
          break;
        case 3:
          typeLabel = this.props.t('seasonTicket');
          break;
        default:
          break;
      }
      footer.push(
        <div>
          {typeLabel}
          {focusedSeat.ex && ','}
        </div>,
      );
    }

    if (focusedSeat.ex) {
      footer.push(focusedSeat.ex.join(', '));
    }
    // debugger;
    return {
      x: x / window.Widget.dpr,
      y: y / window.Widget.dpr,
      price: focusedSeat.price,
      prices: focusedSeat.prices, // ??
      subtitle,
      text,
      offset: 0,
      seat: focusedSeat,
      footer: footer.length ? footer : null,
      statusIcon:
        focusedSeat.at === 1 ? [STATUS_ICONS_NAMES.AVAILABLE] : [STATUS_ICONS_NAMES.PREORDER],
      ticketType: focusedSeat.tt ? [focusedSeat.tt] : [],
      isBrokersTicket: focusedSeat.our,
    };
  }

  initSchemeDraging(event, isMouseEvent = false) {
    if (this.stateController.isLocked) return;

    let pageX;
    let pageY;

    if (isMouseEvent) {
      const eventCoords = getEventCoordinates(event);

      pageX = eventCoords.pageX;
      pageY = eventCoords.pageY;
    } else {
      pageX = event.touches[0].pageX;
      pageY = event.touches[0].pageY;
    }

    if (!window.Widget.draggingController.startX || !window.Widget.draggingController.startY) {
      window.Widget.draggingController.startX = pageX;
      window.Widget.draggingController.startY = pageY;
    }
    const diffX = pageX - window.Widget.draggingController.startX;
    const diffY = pageY - window.Widget.draggingController.startY;

    this.dragXoffset = diffX;
    this.dragYoffset = diffY;

    if (!this.stateController.isDragging && shouldDraggingStart(diffX, diffY)) {
      this.stateController.setState({ isDragging: true });

      isMouseEvent ? this.initMouseDragEndEvents() : this.initTouchDragEndEvents();
    }

    if (this.stateController.isDragging) {
      this.moveScheme(diffX, diffY);
    }
  }

  moveScheme(diffX: number, diffY: number) {
    const viewport = window.Widget.getViewport();
    const { dpr, width: rootWidth, height: rootHeight } = window.Widget;
    const { schemeHeight, schemeWidth } = window.Widget.schemeParameters;
    const { currentZoom, zoomIsMinimum } = this.zoomController;
    const zoom = currentZoom / dpr;

    const currentX = viewport.x - diffX / zoom;
    const currentY = viewport.y - diffY / zoom;
    const currentX2 = viewport.x2 - diffX / zoom;
    const currentY2 = viewport.y2 - diffY / zoom;

    const leftBorderInView = currentX > -rootWidth / 2 / zoom;
    const topBorderInView = currentY > -rootHeight / 2 / zoom;
    const rightBorderInView = currentX2 < schemeWidth / dpr + rootWidth / 2 / zoom;
    const bottomBorderInView = currentY2 < schemeHeight / dpr + rootHeight / 2 / zoom;

    const schemeInHorizontalView = leftBorderInView && rightBorderInView;
    const schemeInVerticalView = topBorderInView && bottomBorderInView;

    if (schemeInHorizontalView && !zoomIsMinimum) {
      this.availableDiffX = diffX;
    }
    if (schemeInVerticalView && !zoomIsMinimum) {
      this.availableDiffY = diffY;
    }

    this.diffX = schemeInHorizontalView ? diffX : diffX / 2;
    this.diffY = schemeInVerticalView ? diffY : diffY / 2;

    this.moveMinimap(this.diffX, this.diffY);

    this.minimapOnDragCallback && this.minimapOnDragCallback(this.diffX, this.diffY);
  }

  endMoveScheme() {
    window.Widget.draggingController.pan({
      offsetX: this.availableDiffX,
      offsetY: this.availableDiffY,
      diffX: this.diffX,
      diffY: this.diffY,
      onEnd: () => {
        this.reset();
        this.stateController.setState({
          isDragging: false,
        });
      },
    });
  }

  onPointerUp(event: MouseEvent | TouchEvent) {
    if (this.stateController.isZooming || this.stateController.isLocked) return;

    const target = event.target as HTMLElement;
    const isMouseEvent = event.type === EVENT_TYPES.MOUSE_EVENT;
    // сидячие места
    if (this.focusedSeat) {
      this.onSeatPointerUp(isMouseEvent);

      return;
    }
    // стоячие места
    if (isStandingSector(target)) {
      this.onStandTouchEnd(target);

      return this.reset();
    }
    // категории
    if (isCategory(target)) {
      this.onCategoryTouchEnd(target);

      return this.reset();
    }

    const { pageX, pageY } = getEventCoordinates(event);
    this.zoomIn(pageX, pageY);

    this.reset();
  }

  onCategoryPointerUp(target: HTMLElement) {
    const id = target.getAttribute('data-id');
    window.Widget.categoriesController.showCategoryModal(id);
  }

  onSeatPointerUp(isMouseEvent = false) {
    if (!isMouseEvent && !window.Widget.metaController.canShowMobilePopup) return;
    if (isMouseEvent && this.stateController.isDragging) return;

    if (this.focusedSeat?.isStand) {
      this.ticketsController.showTicketsCountModal(this.focusedSeat.id);

      this.reset();
    } else {
      this.checkTicket({ isTouch: !isMouseEvent });
      this.reset();
    }
  }

  zoomIn(pageX: number, pageY: number) {
    if (!this.zoomController.zoomIsMaximum && !this.stateController.state.isMinimapMoving) {
      this.stateController.setState({ isZooming: true }, true);

      this.zoomController.zoomIn({
        x: pageX,
        y: pageY,
        onEnd: () => {
          this.stateController.setState({ isZooming: false }, true);
        },
      });
    }
  }

  zoomNode(el: HTMLElement, scaleFactor: number) {
    const { dpr, height, width } = window.Widget;
    el.style.height = `${height / scaleFactor / dpr}px`;
    el.style.width = `${width / scaleFactor / dpr}px`;
    el.style.top = `${
      -1 *
      (this.middlePoint.y / scaleFactor -
        this.middlePoint.y -
        (this.dragYoffset || 0) -
        (this.diffY || 0))
    }px`;
    el.style.left = `${
      -1 *
      (this.middlePoint.x / scaleFactor -
        this.middlePoint.x -
        (this.dragXoffset || 0) -
        (this.diffX || 0))
    }px`;
  }

  checkTicket = ({ isTouch } = { isTouch: false }) => {
    const { selectedTickets } = this.ticketsController;
    const { maxSelectedTickets } = window.Widget.settings;
    const ticket = this.ticketsController.findTicketInSelected(this.focusedSeat);
    const tickets = get(this.focusedSeat, 'meta.tickets');

    if (tickets && tickets.length > 1) {
      return window.Widget.ticketsPopoverController.show(this.focusedSeat);
    }

    if (ticket) {
      if (isTouch && !this.popoverController.state.isHidden) {
        this.popoverController.hide();
      }

      return this.ticketsController.remove(this.focusedSeat);
    }

    if (selectedTickets.length === maxSelectedTickets) {
      return window.Widget.notificationController.error({
        message: this.props.t('maxTicketsToBuy', {
          number: maxSelectedTickets,
        }),
      });
    }

    this.ticketsController.add(this.focusedSeat);
  };

  setOnMinimapDragCallback = (callback: (diffX: number, diffY: number) => void) => {
    this.minimapOnDragCallback = callback;
  };

  reset() {
    this.resetParams();
    this.resetFocusedSeat();
  }

  resetParams() {
    this.newZoom = null;
    this.diffX = 0;
    this.diffY = 0;
    this.availableDiffX = 0;
    this.availableDiffY = 0;
    this.touchDistance = null;
    this.startTouch = false;
    this.dragXoffset = 0;
    this.dragYoffset = 0;

    window.Widget.draggingController.startX = null;
    window.Widget.draggingController.startY = null;

    if (this.touches.length > 1) {
      this.touches = [];
      this.stateController.setState({ isZooming: false }, true);
      return;
    }

    if (this.touches.length) {
      this.touches = [];
      this.stateController.setState({ isZooming: false }, true);
    }
  }

  resetFocusedSeat() {
    this.focusedSeat = null;
  }

  // TODO: fix it
  moveMinimap(diffX: number, diffY: number) {
    window.Widget.staticCanvas.style.left = `${diffX}px`;
    window.Widget.staticCanvas.style.top = `${diffY}px`;
    window.Widget.seatsCanvas.style.left = `${diffX}px`;
    window.Widget.seatsCanvas.style.top = `${diffY}px`;
  }

  disableSelect(event: MouseEvent | TouchEvent) {
    event.preventDefault();
  }

  render() {
    return null;
  }
}

export default SchemeComponent;
